import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  Link,
} from "@mui/material";
import api from "../../services/api";
import { API_ENDPOINTS } from "../../constants/API_ENDPOINTS";

const validationSchema = Yup.object({
  username: Yup.string(),
  email: Yup.string().email("Invalid email address"),
}).test(
  "at-least-one",
  "Either username or email must be provided",
  function (value) {
    return !!(value.username || value.email);
  }
);

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      username: "",
      email: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setError(null);
      setSuccess(null);
      try {
        const response = await api.post(API_ENDPOINTS.REQUEST_PASSWORD_RESET, {
          username: values.username || undefined,
          email: values.email || undefined,
        });
        
        setSuccess(response.data.message || "Password reset link has been sent to your email if an account exists.");
      } catch (err: any) {
        const errorMessage = err.response?.data?.detail || err.message || "Failed to request password reset";
        setError(errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography component="h1" variant="h5">
          Reset Password
        </Typography>
        <Typography variant="body2" sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}>
          Enter your username or email address and we'll send you a link to reset your password.
        </Typography>
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ mt: 1, width: "100%" }}>
          <TextField
            margin="normal"
            fullWidth
            id="username"
            label="Username (optional)"
            name="username"
            autoComplete="username"
            value={formik.values.username}
            onChange={formik.handleChange}
            error={formik.touched.username && Boolean(formik.errors.username)}
            helperText={formik.touched.username && formik.errors.username}
          />
          <Typography variant="body2" sx={{ textAlign: "center", my: 1 }}>
            OR
          </Typography>
          <TextField
            margin="normal"
            fullWidth
            id="email"
            label="Email (optional)"
            name="email"
            type="email"
            autoComplete="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? "Sending..." : "Send Reset Link"}
          </Button>
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate("/login")}
              sx={{ cursor: "pointer" }}
            >
              Back to Login
            </Link>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default ForgotPassword;
